/// <amd-module name="VSSUI/Components/Dropdown/DropdownCallout" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { Button, IButtonProps } from "VSSUI/Button";
import { Callout, ContentSize } from "VSSUI/Callout";
import { FocusZone, FocusZoneDirection, FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { IconSize } from "VSSUI/Icon";
import { ListBox } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import * as Resources from "VSSUI/Resources.Dropdown";
import { TextField } from "VSSUI/TextField";
import { css, getSafeIdSelector, KeyCode, preventDefault } from "VSSUI/Util";
import { IFocusable } from "VSSUI/Utilities/Focus";
import { IDropdownCalloutProps } from "./DropdownCallout.Props";

const ItemsForFilter = 10;
const DefaultWidth = 256;
// This should match the total horizontal padding on bolt-dropdown-filter-container
const FilterBarPadding = 16;
// This should match the width + margin of the textfield search icon
const FilterBarIconWidth = 27;

export function DropdownCallout<T = {}>(props: IDropdownCalloutProps<T>) {
    return <DropdownCalloutComponent<T> {...props} />;
}

interface IDropdownCalloutState {
    scrollBarWidth: number;
}

export class DropdownCalloutComponent<T = {}> extends React.Component<IDropdownCalloutProps<T>, IDropdownCalloutState> implements IFocusable<{}> {
    public static defaultProps = {
        width: DefaultWidth,
        ignoreMouseDown: true
    };

    private callout = React.createRef<Callout>();
    private calloutContent = React.createRef<HTMLDivElement>();
    private filterBox = React.createRef<TextField>();
    private initFocusElement = React.createRef<HTMLDivElement>();

    public constructor(props: IDropdownCalloutProps<T>) {
        super(props);

        this.state = { scrollBarWidth: 0 };
    }

    public componentDidMount() {
        this.getScrollWidth();
    }

    public focus() {
        if (this.filterBox.current) {
            this.filterBox.current.focus();
        } else if (this.initFocusElement.current) {
            this.initFocusElement.current.focus();
        }
    }

    public render() {
        const {
            actions,
            anchorElement,
            anchorOffset,
            anchorOrigin,
            anchorPoint,
            ariaLabel,
            blurDismiss = true,
            calloutContentClassName,
            columns,
            containerClassName,
            contentLocation,
            dropdownOrigin,
            enforceSingleSelect,
            excludeFocusZone,
            excludeTabStop,
            filteredItems,
            filteredNoResultsText,
            filteredResultsLoadingText,
            filterPlaceholderText,
            filterText,
            focusOnMount,
            getUnselectableRanges,
            id,
            items,
            lightDismiss,
            listBoxClassName,
            listBoxRef,
            loading,
            onActivate,
            onFilterKeyDown,
            onFilterTextChanged,
            onSelect,
            onToggle,
            portalProps,
            renderBeforeContent,
            renderItem,
            searching,
            selection,
            showCloseButton,
            showChecksColumn,
            showFilterBox,
            showItemsWhileSearching,
            showTree,
            title,
            updateFilteredItems,
            userFilteredItems
        } = this.props;

        let { width = DefaultWidth } = this.props;
        if (width > 0) {
            width -= this.state.scrollBarWidth;
        }

        const textFieldId = `bolt-dropdown-textfield-${id}`;

        const clearInput = (): void => {
            filterText.value = "";
            if (onFilterTextChanged) {
                onFilterTextChanged(null, "");
            }
            if (updateFilteredItems) {
                updateFilteredItems();
            }
        };

        const onDismiss = () => {
            if (this.props.onDismiss) {
                this.props.onDismiss();
            }
            clearInput();
        };

        return (
            <Callout
                anchorElement={anchorElement}
                anchorOffset={anchorOffset}
                anchorOrigin={anchorOrigin}
                anchorPoint={anchorPoint}
                role="presentation"
                blurDismiss={blurDismiss}
                calloutOrigin={dropdownOrigin}
                contentClassName={css(calloutContentClassName, "bolt-dropdown flex-column custom-scrollbar v-scroll-auto h-scroll-hidden")}
                contentLocation={contentLocation}
                contentRef={this.calloutContent}
                contentShadow={true}
                contentSize={ContentSize.Auto}
                escDismiss={true}
                id={id}
                portalProps={portalProps}
                lightDismiss={lightDismiss}
                focuszoneProps={{
                    postprocessKeyStroke: event => {
                        // dismiss the callout on tab key instead of letting the
                        // browser handle the tab key, since with React.portals it
                        // will move to the body, instead of the next tabbable element after
                        // the dropdown.
                        if (event.which === KeyCode.tab && !event.defaultPrevented) {
                            event.preventDefault();
                            onDismiss();
                            return FocusZoneKeyStroke.IgnoreAll;
                        }
                        return FocusZoneKeyStroke.IgnoreNone;
                    }
                }}
                onDismiss={onDismiss}
                ref={this.callout}
            >
                <FocusZone
                    circularNavigation={true}
                    defaultActiveElement={
                        showFilterBox || (showFilterBox === undefined && items.length > ItemsForFilter)
                            ? getSafeIdSelector(textFieldId)
                            : ".bolt-dropdown-init-focus"
                    }
                    direction={FocusZoneDirection.Vertical}
                    focusOnMount={focusOnMount !== undefined ? focusOnMount : true}
                    preventScrollOnFocus={window.self !== window.top}
                >
                    <div
                        className="bolt-dropdown-container no-outline"
                        onMouseDown={this.onMouseDown}
                        onKeyDown={onFilterKeyDown}
                        style={{ width: width >= 0 ? width : undefined }}
                    >
                        <div
                            aria-hidden="true"
                            aria-roledescription={Resources.DropdownCalloutRoleDescription}
                            className="bolt-dropdown-init-focus no-outline"
                            tabIndex={!excludeTabStop ? -1 : undefined}
                            ref={this.initFocusElement}
                            role="menuitem"
                        />
                        <Observer items={{ observableValue: items, filter: this.updateLayout }}>
                            {() => {
                                const shouldShowFilterBox = showFilterBox === undefined ? items.length > ItemsForFilter : showFilterBox;
                                return shouldShowFilterBox || title || showCloseButton ? (
                                    <div className="bolt-dropdown-header-container">
                                        {(title || showCloseButton) && (
                                            <div className="bolt-dropdown-header flex-row flex-center">
                                                <div className="bolt-dropdown-header-text flex-grow font-weight-semibold">{title}</div>
                                                {showCloseButton && (
                                                    <Button
                                                        className="bolt-dropdown-header-button"
                                                        ariaLabel={Resources.Close}
                                                        iconProps={{ iconName: "Cancel" }}
                                                        onClick={onDismiss}
                                                        subtle={true}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {shouldShowFilterBox && (
                                            <div key="bolt-dropdown-filter-container" className="bolt-dropdown-filter-container">
                                                <Observer filterText={filterText}>
                                                    {(props: { filterText: string }) => {
                                                        return (
                                                            <TextField
                                                                key="bolt-dropdown-filter"
                                                                ariaLabel={Resources.SearchAriaLabel}
                                                                className="bolt-dropdown-filter"
                                                                excludeTabStop={true}
                                                                inputId={textFieldId}
                                                                onChange={onFilterTextChanged}
                                                                placeholder={filterPlaceholderText}
                                                                prefixIconProps={{ iconName: "Search" }}
                                                                ref={this.filterBox}
                                                                value={filterText}
                                                                maxWidth={this.props.width! - FilterBarPadding - FilterBarIconWidth}
                                                                suffixIconProps={
                                                                    props.filterText.length > 0
                                                                        ? {
                                                                              ariaLabel: Resources.ClearText,
                                                                              iconName: "ChromeClose",
                                                                              onClick: clearInput,
                                                                              size: IconSize.small,
                                                                              tooltipProps: {
                                                                                  text: Resources.ClearText
                                                                              },
                                                                              role: "button"
                                                                          }
                                                                        : undefined
                                                                }
                                                            />
                                                        );
                                                    }}
                                                </Observer>
                                            </div>
                                        )}
                                    </div>
                                ) : null;
                            }}
                        </Observer>
                        {renderBeforeContent && renderBeforeContent()}
                        <Observer
                            filteredItems={filteredItems}
                            filteredNoResultsText={filteredNoResultsText}
                            listBoxItems={{ observableValue: items, filter: updateFilteredItems }}
                            userFilteredItems={{ observableValue: userFilteredItems, filter: updateFilteredItems }}
                        >
                            {(props: { filteredNoResultsText: string | undefined }) => {
                                let noItemsElement = null;
                                let noItemsText: string | undefined = "";
                                if (((filteredItems && filteredItems.length === 0) || items.length === 0) && !searching) {
                                    noItemsText =
                                        filterText.value === ""
                                            ? this.props.noItemsText
                                            : format(props.filteredNoResultsText || Resources.NoFilterResults, filterText.value);
                                    if (noItemsText) {
                                        noItemsElement = <div className="bolt-dropdown-no-items">{noItemsText}</div>;
                                    }
                                }

                                return (
                                    <>
                                        {noItemsElement}
                                        <ListBox<T>
                                            ariaLabel={ariaLabel}
                                            className={listBoxClassName}
                                            columns={columns}
                                            containerClassName={css("bolt-dropdown-list-box-container", containerClassName)}
                                            didUpdate={this.listBoxDidUpdate}
                                            enforceSingleSelect={enforceSingleSelect}
                                            excludeFocusZone={excludeFocusZone}
                                            excludeTabStop={true}
                                            searchResultsLoadingText={filteredResultsLoadingText}
                                            focuszoneProps={null}
                                            getUnselectableRanges={getUnselectableRanges}
                                            items={filteredItems ? filteredItems.value : items}
                                            loading={loading}
                                            onActivate={onActivate}
                                            onSelect={onSelect}
                                            onToggle={onToggle}
                                            renderItem={renderItem}
                                            ref={listBoxRef}
                                            searching={searching}
                                            selection={selection}
                                            showChecksColumn={showChecksColumn === undefined ? true : showChecksColumn}
                                            showItemsWhileSearching={showItemsWhileSearching}
                                            showTree={showTree}
                                        />
                                    </>
                                );
                            }}
                        </Observer>
                        <Observer actions={actions}>
                            {(props: { actions: IButtonProps[] | undefined }) => {
                                const actions = this.props.actions;
                                return actions && actions.length ? (
                                    <div className="bolt-actions-container flex-column">
                                        {ObservableLike.getValue(actions).map((actionProps, index) => (
                                            <Button key={actionProps.id || index} subtle={true} excludeTabStop={true} {...actionProps} />
                                        ))}
                                    </div>
                                ) : null;
                            }}
                        </Observer>
                    </div>
                </FocusZone>
            </Callout>
        );
    }

    private updateLayout = (): boolean => {
        // Allow the new items to draw before updating the layout.
        setTimeout(() => {
            if (this.callout.current) {
                this.callout.current.updateLayout();
            }
        }, 0);

        return true;
    };

    private onMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (this.props.ignoreMouseDown) {
            if ((event.target as HTMLElement).tagName !== "INPUT") {
                preventDefault(event);
            }
        }
    };

    private listBoxDidUpdate = () => {
        this.getScrollWidth();
    };

    private getScrollWidth = () => {
        window.requestAnimationFrame(() => {
            if (this.calloutContent.current && this.props.width! >= 0) {
                const widthChange = this.calloutContent.current.offsetWidth - this.props.width!;
                // A 1 pixel change in the width may change the total width by 2 pixels if there are two scroll bars.
                // Only rerender when a scrollbar is appearing or being removed which should be > 1 pixel change.
                if (Math.abs(widthChange) > 1) {
                    this.setState({ scrollBarWidth: widthChange + this.state.scrollBarWidth });
                }
            }
        });
    };
}
